import { initPagingOptions } from '@/coreDomainsModule/paging';
import { IInventoryState } from './type';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { MONTH_DISTANCE_ARRAY } from '@/InventoryControlModule/constants';
import { YearMonthRange } from '@/coreDomainsModule/entities/yearMonthRange';
import { diContainer } from '@/coreContainer/container';

/**
 * 在庫管理画面の初期状態を生成する関数
 * @returns 初期状態のIInventoryStateオブジェクト
 */
export const initInventoryState = (): IInventoryState => {
  return {
    monthlyData: {},
    isOpeningBusinessId: '',
    businessList: [],
    tableLoadingState: {
      status: {
        type: 'none',
      },
    },
    editOrderInfoLoadingState: {
      status: {
        type: 'none',
      },
    },
    pagingOptions: initPagingOptions(30),
    tableInfos: {},
    resizerState: undefined,
    pageMode: 'normal',
    orderEditData: {},
    workDaysCalendarNotYetEnteredAlertModalStatus: false,
    headerList: [],
    editMemoLoadingState: {
      state: {
        status: {
          type: 'none',
        },
      },
      dataType: 'none',
      isDelete: false,
    },
    isCheckAllRows: false,
    checkedAggregateBusinessIds: [],
    isOpenGroupMenuDropdown: false,
    selectedGroupMenu: undefined,
    editGroupModeState: {
      updateGroupNameModalState: false,
      selectedEditGroupId: '',
      selectedEditGroupName: '',
    },
    groupLoadingState: {
      type: 'none',
      state: {
        status: {
          type: 'none',
        },
      },
    },
    moveWarehouseModalState: {
      isOpen: false,
      changedBusiness: undefined,
      otherBusinesses: [],
      loadingStatus: {
        status: {
          type: 'success',
        },
      },
      isSuccessModalOpen: false,
      isLoadingModalOpen: false,
    },
    isOpeningAlertBusinessId: '',
    headerSortingState: {
      key: 'aggregateBusinessId',
      isAsc: true,
    },
    hiddenTransactionHeaderList: [],
    summaryData: {
      arrivalSummary: [],
      customerForecastSummary: [],
      inventorySummary: [],
      orderSummary: [],
      salesResultSummary: [],
    },

    // 内示正答率関連の初期状態
    forecastAccuracyData: {},
    forecastAccuracyModalState: {
      isOpen: false,
      targetBusinessId: undefined,
      historyData: undefined,
      loadingState: {
        type: 'zero-match',
      },
    },
    businessRowMenuState: {
      businessId: '',
      item: undefined,
      businessType: 'none',
      edit: {
        isEditingModalOpen: false,
        isCreateMode: false,
      },
      editTransaction: {
        isEditTransactionMode: false,
        changedValue: [],
      },
      orderBasis: {
        modalState: false,
      },
    },
    combinedOrdersState: {
      settingModalStatus: 'close',
      combinedOrderContents: {
        groupName: '',
        orderCount: 0,
        totalAmount: 0,
        maxTotalAmount: 0,
        desiredDeliveryDate: {
          year: -1,
          month: -1,
          day: -1,
        },
        loadFactor: 0,
        confirmedOrdersWithBusinessAndAdjustment: [],
      },
    },
    selectDisplayInventoryYearMonthRangeModalState: {
      isOpen: false,
      startYearMonth: DateService.getShiftedIYearMonthFromNowDateByMonths(-3),
      endYearMonth: DateService.getShiftedIYearMonthFromNowDateByMonths(-3),
    },
    transactionMonths: MONTH_DISTANCE_ARRAY, // 月の距離を定義
    editUnpaidOrderedOrderModalState: {
      isOpening: false,
      executionDate: undefined,
      unpaidOrderedOrder: [],
    },
    businessFreeColumnHeaders: [],
  };
};

/**
 * 在庫管理画面の開始年と終了年を初期化する関数
 * @returns 開始年と終了年の月を含むオブジェクト
 * @description IndexedDBから情報を取得する、情報がない場合は現在の年月から3ヶ月前から3ヶ月後までの範囲を設定する
 */
export const initStartAndEndYearMonth = async (): Promise<YearMonthRange> => {
  // Preferenceから情報の取得を行う
  const yearMonthRangeInfos = await diContainer.yearMonthRangeInfoRepository.get();
  if (!yearMonthRangeInfos || !yearMonthRangeInfos['inventoryControl']) {
    const startYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(-3);
    const endYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(3);
    return { startYearMonth: startYearMonth, endYearMonth: endYearMonth };
  } else {
    const yearMonthRangeInfo: YearMonthRange = yearMonthRangeInfos['inventoryControl'];
    return yearMonthRangeInfo;
  }
};
